// Archivio Impegno Sociale (archive-social_impact.php): due componenti
// indipendenti nella stessa sezione, ciascuno no-op se il proprio markup non
// e' in pagina.
//
// 1) initArchivioImpegnoSocialeGrid(): "Carica altre iniziative" + ricerca
//    live + filtro per ambito (tassonomia impegno_ambito), sostenuti
//    dall'endpoint REST castro/v1/impegno-sociale
//    (inc/impegno-sociale-rest-controller.php) - stesso principio di
//    assets/js/archivio-collaborazioni.js (vedi quel file per i commenti
//    estesi sul perche' il markup PHP renderizza server-side solo la prima
//    pagina, con la paginazione standard di WordPress come fallback senza
//    JS).
//
// 2) initArchivioImpegnoSocialeFeatured(): carosello a slide singola della
//    sezione "Progetto continuativo" (template-parts/archivio-impegno-sociale/
//    featured.php), mostrata solo quando 2+ iniziative sono marcate "in
//    corso" (impegno_in_evidenza). Il tema non include nessuna libreria di
//    carousel (Swiper, Slick, ...): qui una implementazione minima fatta in
//    casa, sullo stesso stile "vanilla" del resto del sito - vedi
//    assets/js/gallery.js per il precedente piu' vicino (frecce + contatore,
//    niente libreria) di un componente di navigazione fatto a mano.
import { animateCardsIn, animateCardsOut } from "./archivio-card-animations";

const SEARCH_DEBOUNCE_MS = 300;

function debounce(fn, delay) {
    let timer = null;

    return function debounced(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
    };
}

function initArchivioImpegnoSocialeGridSection(section) {
    const grid = section.querySelector("[data-impegno-grid]");

    if (!grid) {
        return;
    }

    const searchInput = section.querySelector("[data-impegno-search-input]");
    const emptyMessage = section.querySelector("[data-impegno-search-empty]");
    const countLabel = section.querySelector("[data-impegno-count]");
    const loadMoreWrap = section.querySelector("[data-impegno-load-more-wrap]");
    const loadMoreButton = section.querySelector("[data-impegno-load-more]");
    const showLessButton = section.querySelector("[data-impegno-show-less]");
    const shownCountLabel = section.querySelector("[data-impegno-shown-count]");
    const loadMoreError = section.querySelector("[data-impegno-load-more-error]");
    const pagination = section.querySelector(".archivio-impegno-sociale__pagination");
    const filterButtons = Array.from(section.querySelectorAll("[data-impegno-filter]"));

    const settings = (window.Castro && window.Castro.settings) || {};
    const apiUrl = `${settings.api_url || ""}castro/v1/impegno-sociale`;

    // Quanto e' gia' renderizzato server-side (vedi $impegno_shown in
    // archive-social_impact.php): e' anche la dimensione a cui torna "Mostra
    // meno".
    const baseCount = parseInt(loadMoreWrap ? loadMoreWrap.dataset.impegnoShown : "0", 10) || 0;

    let shown = baseCount;
    let total = parseInt(section.dataset.impegnoTotal, 10) || 0;
    let currentSearch = "";
    let currentAmbito = "";
    // Scarta le risposte di richieste superate (l'utente ha digitato/cliccato
    // altro nel frattempo): solo l'ultima richiesta partita puo' aggiornare il DOM.
    let requestId = 0;

    // Con JS attivo la paginazione classica (unico modo di sfogliare l'elenco
    // senza JS) lascia il posto a "Carica altre iniziative"/ricerca/filtro.
    if (pagination) {
        pagination.hidden = true;
    }

    function updateCount(value) {
        if (!countLabel) {
            return;
        }

        const label = value === 1 ? "iniziativa" : "iniziative";
        countLabel.textContent = `${value} ${label}`;
    }

    function updateLoadMoreUi(hasMore) {
        if (loadMoreWrap) {
            loadMoreWrap.hidden = total <= baseCount;
        }

        if (loadMoreButton) {
            loadMoreButton.hidden = !hasMore;
        }

        if (showLessButton) {
            showLessButton.hidden = shown <= baseCount;
        }

        if (shownCountLabel) {
            shownCountLabel.textContent = total ? `Mostrate ${shown} di ${total} iniziative` : "";
        }
    }

    async function fetchImpegnoSociale({ search, ambito, offset, perPage }) {
        const params = new URLSearchParams({
            search,
            ambito,
            offset: String(offset),
            per_page: String(perPage),
        });

        const response = await fetch(`${apiUrl}?${params.toString()}`, {
            headers: { Accept: "application/json" },
        });

        if (!response.ok) {
            throw new Error(`Richiesta fallita (${response.status})`);
        }

        return response.json();
    }

    function runQuery() {
        const thisRequestId = ++requestId;
        const perPage = baseCount;

        fetchImpegnoSociale({ search: currentSearch, ambito: currentAmbito, offset: 0, perPage })
            .then((data) => {
                if (thisRequestId !== requestId) {
                    return;
                }

                grid.innerHTML = data.html;
                shown = data.shown;
                total = data.total;

                if (emptyMessage) {
                    emptyMessage.hidden = total > 0;
                }

                updateCount(total);
                updateLoadMoreUi(data.hasMore);
                animateCardsIn(Array.from(grid.children));
            })
            .catch((error) => {
                // Ricerca/filtro non critici: se la richiesta fallisce si
                // lascia la griglia com'era, senza rompere il resto della pagina.
                console.error("[archivio-impegno-sociale]", error);
            });
    }

    const debouncedSearch = debounce(runQuery, SEARCH_DEBOUNCE_MS);

    if (searchInput) {
        searchInput.addEventListener("input", () => {
            currentSearch = searchInput.value.trim();
            debouncedSearch();
        });
    }

    filterButtons.forEach((button) => {
        button.addEventListener("click", () => {
            if (button.classList.contains("is-active")) {
                return;
            }

            currentAmbito = button.dataset.impegnoFilterValue || "";
            filterButtons.forEach((btn) => btn.classList.toggle("is-active", btn === button));
            runQuery();
        });
    });

    if (loadMoreButton) {
        loadMoreButton.addEventListener("click", () => {
            const thisRequestId = ++requestId;

            if (loadMoreError) {
                loadMoreError.hidden = true;
            }
            loadMoreButton.classList.add("is-loading");

            fetchImpegnoSociale({ search: currentSearch, ambito: currentAmbito, offset: shown, perPage: baseCount })
                .then((data) => {
                    if (thisRequestId !== requestId) {
                        return;
                    }

                    const previousCardCount = grid.children.length;

                    // data.shown e' gia' "offset + card restituite in questa
                    // risposta" (calcolato server-side): sostituisce shown
                    // per intero, non si somma a quello precedente.
                    grid.insertAdjacentHTML("beforeend", data.html);
                    shown = data.shown;
                    total = data.total;
                    updateLoadMoreUi(data.hasMore);
                    animateCardsIn(Array.from(grid.children).slice(previousCardCount));
                })
                .catch((error) => {
                    console.error("[archivio-impegno-sociale]", error);
                    if (loadMoreError) {
                        loadMoreError.hidden = false;
                    }
                })
                .finally(() => {
                    if (thisRequestId === requestId) {
                        loadMoreButton.classList.remove("is-loading");
                    }
                });
        });
    }

    if (showLessButton) {
        showLessButton.addEventListener("click", () => {
            const cards = Array.from(grid.querySelectorAll(".archivio-impegno-sociale__card")).slice(baseCount);

            showLessButton.classList.add("is-loading");
            animateCardsOut(cards, () => {
                showLessButton.classList.remove("is-loading");
                shown = baseCount;
                updateLoadMoreUi(total > baseCount);
            });
        });
    }

    updateLoadMoreUi(total > shown);
}

function initArchivioImpegnoSocialeFeaturedSection(section) {
    const track = section.querySelector("[data-impegno-featured-track]");
    const slides = Array.from(section.querySelectorAll("[data-impegno-featured-slide]"));

    if (!track || slides.length < 2) {
        return;
    }

    const prevButton = section.querySelector("[data-impegno-featured-prev]");
    const nextButton = section.querySelector("[data-impegno-featured-next]");
    const dots = Array.from(section.querySelectorAll("[data-impegno-featured-dot]"));

    let index = 0;

    function goTo(nextIndex) {
        index = (nextIndex + slides.length) % slides.length;
        track.style.transform = `translateX(-${index * 100}%)`;

        dots.forEach((dot, dotIndex) => {
            dot.classList.toggle("is-active", dotIndex === index);
        });
    }

    if (prevButton) {
        prevButton.addEventListener("click", () => goTo(index - 1));
    }

    if (nextButton) {
        nextButton.addEventListener("click", () => goTo(index + 1));
    }

    dots.forEach((dot) => {
        dot.addEventListener("click", () => {
            const dotIndex = parseInt(dot.dataset.impegnoFeaturedIndex, 10) || 0;
            goTo(dotIndex);
        });
    });

    // Swipe touch di base, per chi naviga da mobile: solo orizzontale, con
    // una soglia minima per non scattare su un tocco quasi verticale
    // (scroll della pagina).
    let touchStartX = 0;

    track.addEventListener(
        "touchstart",
        (event) => {
            touchStartX = event.touches[0].clientX;
        },
        { passive: true }
    );

    track.addEventListener(
        "touchend",
        (event) => {
            const delta = event.changedTouches[0].clientX - touchStartX;

            if (Math.abs(delta) < 40) {
                return;
            }

            goTo(delta < 0 ? index + 1 : index - 1);
        },
        { passive: true }
    );

    goTo(0);
}

export function initArchivioImpegnoSociale() {
    document.querySelectorAll("[data-archivio-impegno-sociale]").forEach(initArchivioImpegnoSocialeGridSection);
    document.querySelectorAll("[data-impegno-featured]").forEach(initArchivioImpegnoSocialeFeaturedSection);
}
